import { IdentityCheckStatus } from '@afterpaytouch/portal-api'
import { defaultOrdersParams } from '@afterpaytouch/portal-api/consumer/ordertransactions'
import { TFunction, useTranslation } from 'next-i18next'
import { useCallback } from 'react'

import {
  useConsumerIdentityIsValid,
  useConsumerHasPreferredPaymentMethod,
  useOrdersHasTransactions,
  useConsumerGivenName,
  useGetCreditLimitQuery,
  useConsumerLocaleHardIdentityCheckIsRequired,
  useConsumerIdentityStatus,
  useConsumerCreditLimitOverride,
  useGetOrderTransactionsQuery,
} from '../../../state'
import { Money as IMoney } from '@afterpay/types'
import { getIdentityVerifyErrorCode, IdentityErrorCode } from '@afterpaytouch/portal-api/identity'
import { isTerminalErrorCode } from '../../../config/router'

const I18N_NAMESPACE = ['common', 'index']

export const useNewUserLocalization = (): {
  t: TFunction
  locale: string
} => {
  const {
    t,
    i18n: { language },
  } = useTranslation(I18N_NAMESPACE)

  return { t, locale: language }
}

export const isHandledCreditLimitError = (errorCode?: IdentityErrorCode | null): boolean => {
  return isUnsuitabilityRequiredError(errorCode)
}

export const isUnsuitabilityRequiredError = (errorCode?: IdentityErrorCode | null): boolean => {
  return errorCode === IdentityErrorCode.UnsuitabilityNotFound || errorCode === IdentityErrorCode.UnsuitabilityExpired
}

export const useNewUserEffects = (): {
  consumer: {
    isVerified: boolean
    isLocaleHardIdentityCheckRequired: boolean
    identityCheckStatus: IdentityCheckStatus
    hasPreferredPaymentMethod: boolean
    hasTransactions: boolean
    givenName: string
    availableToSpend: IMoney
  }
  creditLimitErrorCode?: IdentityErrorCode | null
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  refetchNewUser: () => void
  isTerminalError: boolean
} => {
  const isConsumerVerified = useConsumerIdentityIsValid()
  const {
    data: creditLimitData,
    error: creditLimitError,
    isLoading: isCreditLimitLoading,
    isFetching: isCreditLimitFetching,
    isError: isCreditLimitError,
    refetch: refetchGetCreditLimitQuery,
  } = useGetCreditLimitQuery(undefined, { skip: !isConsumerVerified })
  const creditLimitErrorCode = getIdentityVerifyErrorCode(creditLimitError)
  const isUnhandledCreditLimitError = isCreditLimitError && !isHandledCreditLimitError(creditLimitErrorCode)
  const isTerminalError = creditLimitErrorCode ? isTerminalErrorCode(creditLimitErrorCode) : false

  const {
    isFetching: isGetOrderTransactionsQueryFetching,
    isLoading: isGetOrderTransactionsQueryLoading,
    isError: isGetOrderTransactionsQueryError,
    refetch: refetchGetOrderTransactionsQuery,
  } = useGetOrderTransactionsQuery(defaultOrdersParams, {
    refetchOnFocus: false,
  })

  const refetchNewUser = useCallback((): void => {
    if (isUnhandledCreditLimitError) {
      refetchGetCreditLimitQuery()
    }
    if (isGetOrderTransactionsQueryError) {
      refetchGetOrderTransactionsQuery()
    }
  }, [isUnhandledCreditLimitError, isGetOrderTransactionsQueryError, refetchGetCreditLimitQuery, refetchGetOrderTransactionsQuery])

  return {
    consumer: {
      isVerified: isConsumerVerified, // @TODO: We will likely have to use the identity verify API here as apparently the ID status returned here cannot be relied on
      isLocaleHardIdentityCheckRequired: useConsumerLocaleHardIdentityCheckIsRequired(),
      // @ts-ignore: OPERATION BLEED STOPPER
      identityCheckStatus: useConsumerIdentityStatus(),
      hasPreferredPaymentMethod: useConsumerHasPreferredPaymentMethod(),
      hasTransactions: useOrdersHasTransactions(),
      // @ts-ignore: OPERATION BLEED STOPPER
      givenName: useConsumerGivenName(),
      // @ts-ignore: OPERATION BLEED STOPPER
      availableToSpend: useConsumerCreditLimitOverride(creditLimitData?.creditLimit),
    },
    creditLimitErrorCode,
    isLoading: isCreditLimitLoading || isGetOrderTransactionsQueryLoading,
    isFetching: isCreditLimitFetching || isGetOrderTransactionsQueryFetching,
    isError: isUnhandledCreditLimitError || isGetOrderTransactionsQueryError,
    refetchNewUser,
    isTerminalError,
  }
}
